/**
 * Cell content for configured grid columns (status pills, initials avatars).
 *
 * The grid renders inside a Material UI ThemeProvider that knows nothing about Joy UI, so a Joy
 * component used here fails (its theme variants are missing). These cells are plain elements styled
 * with Joy's global CSS variables (`--joy-palette-*`), which exist in the DOM whatever the React
 * theme context is and follow the canvas theme: the pill and avatar look like the Joy soft chip and
 * avatar used elsewhere on the canvas. No colour literal here (the token-purity test forbids them).
 */
import React, { createContext } from "react";
import { statusColorFor } from "../canvasBuilder/elements/StatusChip";
import { initialsFor } from "../canvasBuilder/elements/InitialsAvatar";
import type { StatusColor } from "../canvasBuilder/canvasStyleOverrides";

/** Decorates an existing enum editor without changing its options or write behaviour. */
export const ColumnStatusColorsContext = createContext<
  Record<string, StatusColor> | undefined
>(undefined);

/** A status value as a soft coloured pill. Renders nothing for an empty value. */
export function GridStatusPill({
  value,
  statusColors,
}: {
  value: string;
  statusColors?: Record<string, StatusColor>;
}) {
  if (!value) return null;
  const color = statusColorFor(value, statusColors);
  return (
    <span
      data-testid="status-chip"
      data-status-color={color}
      style={{
        display: "inline-flex",
        alignItems: "center",
        height: 24,
        padding: "0 10px",
        borderRadius: "var(--joy-radius-lg, 12px)",
        fontSize: "var(--joy-fontSize-xs, 12px)",
        fontWeight: 500,
        lineHeight: 1,
        whiteSpace: "nowrap",
        background: `var(--joy-palette-${color}-softBg)`,
        color: `var(--joy-palette-${color}-softColor)`,
      }}
    >
      {value}
    </span>
  );
}

/** A person's name with a small initials avatar in front of it. Renders nothing for a blank name. */
export function GridInitialsAvatar({ name }: { name: string }) {
  const initials = initialsFor(name);
  if (!initials) return null;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        minWidth: 0,
      }}
    >
      <span
        data-testid="initials-avatar"
        aria-hidden
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "none",
          width: 28,
          height: 28,
          borderRadius: "50%",
          fontSize: "var(--joy-fontSize-xs, 12px)",
          fontWeight: 600,
          background: `var(--joy-palette-primary-softBg)`,
          color: `var(--joy-palette-primary-softColor)`,
        }}
      >
        {initials}
      </span>
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {name}
      </span>
    </span>
  );
}
